"use client";

import Link from "next/link";
import { useState, useTransition } from "react";
import { useSearchParams } from "next/navigation";
import { createCheckoutSession } from "@/lib/actions/checkout";
import { formatCurrencyEUR, normalizeLocale, withLang } from "@/lib/i18n";
import {
  useCartActions,
  useCartItems,
  useTotalPrice,
} from "@/lib/store/cart-store-provider";

export function CheckoutSummary() {
  const searchParams = useSearchParams();
  const locale = normalizeLocale(searchParams.get("lang"));
  const items = useCartItems();
  const total = useTotalPrice();
  const { removeItem, updateQuantity } = useCartActions();
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  const startCheckout = () => {
    setError(null);

    startTransition(async () => {
      const result = await createCheckoutSession(items, locale);
      if (!result.success || !result.url) {
        setError(result.error ?? "Unable to start checkout right now.");
        return;
      }
      window.location.href = result.url;
    });
  };

  if (items.length === 0) {
    return (
      <div className="rounded-[2rem] border border-[#D9D1C6] bg-[#FBFAF7] p-6 sm:p-8">
        <h2 className="text-2xl text-[#2B2824]">{locale === "pt-PT" ? "O carrinho está vazio" : "Your cart is empty"}</h2>
        <p className="mt-2 text-sm leading-7 text-[#666058]">{locale === "pt-PT" ? "Adicione produtos da loja para iniciar o checkout com Stripe." : "Add products from the shop to begin your Stripe checkout flow."}</p>
        <Link href={withLang("/shop", locale)} className="mt-5 inline-flex rounded-full bg-[#4F4A43] px-5 py-2 text-sm font-medium text-[#F8F6F1]">
          {locale === "pt-PT" ? "Ir para a loja" : "Go to shop"}
        </Link>
      </div>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[1.8fr_1fr]">
      <div className="rounded-[2rem] border border-[#D9D1C6] bg-[#FBFAF7] p-5 sm:p-6">
        <h2 className="text-2xl text-[#2B2824]">{locale === "pt-PT" ? "Checkout" : "Checkout"}</h2>
        <p className="mt-1 text-sm leading-7 text-[#666058]">
          {locale === "pt-PT" ? "Reveja os artigos antes de continuar para o pagamento Stripe." : "Review your items before continuing to Stripe payment."}
        </p>

        <div className="mt-5 space-y-4">
          {items.map((item) => (
            <div key={item.productId} className="flex flex-wrap items-center justify-between gap-3 rounded-[1.5rem] border border-[#E3DDD3] bg-[#F8F5EF] p-4">
              <div>
                <p className="font-medium text-[#2B2824]">{item.name}</p>
                <p className="text-sm text-[#666058]">
                  {formatCurrencyEUR(item.price, locale)} {locale === "pt-PT" ? "cada" : "each"}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 rounded-full border border-[#D9D1C6] px-2 py-1">
                  <button
                    onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                    className="h-6 w-6 rounded-full text-sm font-bold text-[#2B2824]"
                    aria-label={`Decrease quantity for ${item.name}`}
                  >
                    -
                  </button>
                  <span className="min-w-5 text-center text-sm text-[#2B2824]">{item.quantity}</span>
                  <button
                    onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                    className="h-6 w-6 rounded-full text-sm font-bold text-[#2B2824]"
                    aria-label={`Increase quantity for ${item.name}`}
                  >
                    +
                  </button>
                </div>
                <button onClick={() => removeItem(item.productId)} className="text-xs font-medium text-[#4F4A43] underline-offset-4 hover:underline">
                  {locale === "pt-PT" ? "Remover" : "Remove"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <aside className="h-fit rounded-[2rem] border border-[#D9D1C6] bg-[#FBFAF7] p-5 sm:p-6">
        <h3 className="text-xl text-[#2B2824]">{locale === "pt-PT" ? "Resumo" : "Summary"}</h3>
        <div className="mt-4 space-y-2 text-sm text-[#666058]">
          <div className="flex justify-between">
            <span>{locale === "pt-PT" ? "Subtotal" : "Subtotal"}</span>
            <span>{formatCurrencyEUR(total, locale)}</span>
          </div>
          <div className="flex justify-between">
            <span>{locale === "pt-PT" ? "Envio" : "Shipping"}</span>
            <span>{locale === "pt-PT" ? "Calculado no Stripe" : "Calculated in Stripe"}</span>
          </div>
        </div>

        <div className="mt-4 border-t border-[#D9D1C6] pt-4">
          <div className="flex justify-between text-sm font-medium text-[#2B2824]">
            <span>{locale === "pt-PT" ? "Total" : "Total"}</span>
            <span>{formatCurrencyEUR(total, locale)}</span>
          </div>
        </div>

        <button
          onClick={startCheckout}
          disabled={isPending}
          className="mt-5 w-full rounded-full bg-[#4F4A43] px-4 py-2.5 text-sm font-medium text-[#F8F6F1] transition hover:bg-[#666058] disabled:opacity-60"
        >
          {isPending
            ? locale === "pt-PT"
              ? "A preparar pagamento..."
              : "Preparing payment..."
            : locale === "pt-PT"
              ? "Pagar com Stripe"
              : "Pay with Stripe"}
        </button>

        <Link href={withLang("/payment", locale)} className="mt-3 block text-center text-xs font-medium text-[#666058] underline-offset-4 hover:underline">
          {locale === "pt-PT" ? "Ver detalhes da integração de pagamento" : "See payment integration details"}
        </Link>

        {error ? <p className="mt-3 text-xs text-red-700">{error}</p> : null}
      </aside>
    </div>
  );
}
